import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { BranchService, buildPersonalIdValidation, ClassificationCodeService, NotificationService, UserService, UserSourceService, toCalendarDate } from '@beautyapp/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { DatepickerComponent, InputComponent, SelectComponent, TextareaComponent } from '@beautyapp/form';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
  selector: 'beautyapp-add-appointment-user',
  templateUrl: './add-appointment-user.component.html',
  styleUrls: ['./add-appointment-user.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzGridModule,
    InputComponent,
    SelectComponent,
    DatepickerComponent,
    TextareaComponent,
    TranslateModule,
    TranslatePipe,
    NzButtonModule,
    NzModalModule
  ]
})
export class AddAppointmentUserComponent implements OnInit, OnDestroy {
  modal = inject(NzModalRef);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private fb = inject(UntypedFormBuilder);
  private store = inject(Store);
  private branchService = inject(BranchService);

  // Personal-ID validation is driven by the per-branch config (beaut-315):
  // { enabled, required, minLength, maxLength }. Applied dynamically in ngOnInit.
  personalIdRequired = false;
  personalIdError = 'validators.personalId11Digits';


  readonly data = inject(NZ_MODAL_DATA);
  readonly classificationCodeService = inject(ClassificationCodeService);
  readonly userSourceService = inject(UserSourceService);

  genders = this.classificationCodeService.getGender();
  bloodGroups = this.classificationCodeService.getBloodGroup();
  userSources = toSignal(this.userSourceService.dropdown(), { initialValue: [] });

  get userId() {
    return this.data.userId;
  }

  form: UntypedFormGroup;
  sub$: Subject<any> = new Subject<any>();

  constructor() {
    this.form = this.fb.group({
      cardNo: null,
      firstname: [null, Validators.required],
      lastname: [null],
      gender: null,
      mobilePhone: [null, Validators.required],
      // Validators applied dynamically from the branch personal-id config (ngOnInit).
      identityNumber: [null, []],
      birthDate: null,
      city: null,
      address: null,
      workplace: null,
      userSourceId: null,
      clinic: this.fb.group({
        allergy: null,
        bronchialAsthma: false,
        arterialHypertension: false,
        hepatitisABC: false,
        epilepsy: false,
        tuberculosis: false,
        diabetesMellitus: false,
        thyroidPathology: false,
        pregnancy: false
      })
    });
  }

  ngOnInit(): void {
    const branch = this.store.selectSnapshot(AuthState.getActiveBranch);
    if (branch?.id) {
      this.branchService.getPersonalIdConfig(branch.id).subscribe({
        next: (cfg) => this.applyPersonalIdConfig(cfg),
        error: () =>
          this.applyPersonalIdConfig({
            enabled: !!branch?.personalIdValidationEnabled,
            required: true,
            minLength: 11,
            maxLength: 11,
          }),
      });
    }
  }

  private applyPersonalIdConfig(cfg: any): void {
    const { validators, required, error } = buildPersonalIdValidation(cfg);
    this.personalIdRequired = required;
    this.personalIdError = error;
    const ctrl = this.form.get('identityNumber');
    ctrl?.setValidators(validators);
    ctrl?.updateValueAndValidity();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.userService
      .create({ ...this.form.value, birthDate: toCalendarDate(this.form.value.birthDate) })
      .pipe(takeUntil(this.sub$))
      .subscribe({
        next: (res) => this.modal.close(res),
        error: (err) => {
          const message = err?.error?.message || err?.message;
          if (message) {
            this.notificationService.warning(message, 'პირადი ნომერი');
          }
        },
      });
  }

  checkUser() {
    const { mobilePhone } = this.form.value;
    if (mobilePhone && mobilePhone.length >= 9) {
      this.userService
        .checkUser({
          mobilePhone
        })
        .pipe(takeUntil(this.sub$))
        .subscribe((res) => {
          if (res) {
            this.notificationService.warning(
              'მსგავსი მობლურის ნორმით მომხმარებელი უკვე არსებობს',
              'მომხმარებლის ნომრის შემოწმება'
            );
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }
}
